<template>
  <AppModalWithConfirmation
    ref="appModalWithConfirmation"
    :confirm-close="areDataChanged"
    :density="DIALOG_DENSITY.COMPACT"
    :show="show"
    :size="607"
    :title="modalTitle"
    hide-hero
    @on-close="handleModalClose"
  >
    <template #footer-actions>
      <ModalFooterActions
        :disabled="isSubmitDisabled"
        :loading="loading"
        hide-checkbox
        @close="handleCancel"
        @create="handleSave"
      >
        <template #confirm-button-text>
          {{ $t('action.save') }}
        </template>
      </ModalFooterActions>
    </template>

    <div class="ctm-ContentWrapper">
      <FormFieldNext :label="$t('schedule.icon_plus_title')" required>
        <div class="ctm-TitleIconWrapper">
          <AppEmojiPicker
            ref="emojiPickerRef"
            :disable="isTemplateDataLoading"
            position="bottom-start"
            @select-emoji="handleEmojiSelect"
          >
            <template #button>
              <SkeletonItem
                v-if="isTemplateDataLoading"
                :appearance="SKELETON_APPEARANCES.SQUARE"
                :size="SKELETON_SIZES.LG"
              />

              <AppButtonNext
                v-else
                :size="BUTTON_SIZES_NEXT.XL"
                :variant="BUTTON_VARIANTS.SECONDARY_GRAY"
                class="ctm-IconButton"
                no-padding
              >
                {{ formModel[EMOJI] }}
              </AppButtonNext>
            </template>
          </AppEmojiPicker>
          <AppInput
            v-model="formModel[TITLE]"
            :auto-focus="!isEdit"
            :loading="isTemplateDataLoading"
            :max-length="TITLE_MAX_LENGTH"
            :placeholder="$t('checkins.enter_title')"
            class="ctm-TitleInput"
            size="xlg"
            style-type="primary"
          />
        </div>
      </FormFieldNext>

      <SkeletonItem v-if="isTemplateDataLoading" :size="SKELETON_SIZES.LG" width="100%" />

      <GlobalGroupsWorkspaceSelect
        v-else
        v-model:selected-workspaces="formModel[WORKSPACE_IDS]"
        hide-all-workspaces-option-in-selected-items-list
        required
      />

      <FormFieldNext :label="$t('schedule.template_text')">
        <SkeletonItem v-if="isTemplateDataLoading" height="103px" width="100%" />

        <DescriptionField
          v-else
          v-model="formModel[TEXT]"
          :actions="false"
          :blur-on-esc="false"
          :hidden-features="[TIPTAP_EXTENSION_NAMES.MENTION]"
          :max-length="DESCRIPTION_FILED_LIMITS.COMMENT"
          :placement="COMMENT_FIELD_PLACEMENTS"
          class="ctm-DescriptionField"
          hard-reset-on-empty
          show-on-init
          simple-view
          @characters-limit-exceed="isContentLimitExceeded = $event"
        />
      </FormFieldNext>
    </div>
  </AppModalWithConfirmation>
</template>

<script setup>
import { cloneDeep, isEqual, isEmpty, isNull } from 'lodash'
import { computed, ref, toValue, useTemplateRef, watch } from 'vue'
import { useI18n } from 'vue-i18n'

import CheckinTemplatesApiHandler from '@/api/checkin-templates'
import { EVENT_NAMES } from '@/tracking/gtm-helpers'
import { gtmTracker } from '@/tracking/gtm-tracking'
import { BUTTON_SIZES_NEXT, BUTTON_VARIANTS } from '@/utils/components-configurations/app-button'
import { DIALOG_DENSITY } from '@/utils/components-configurations/app-dialog'
import {
  SKELETON_APPEARANCES,
  SKELETON_SIZES
} from '@/utils/components-configurations/skeleton-item'
import { CHECKIN_TEMPLATE_ENTITY_KEYS } from '@/utils/entity-keys'
import { handleError } from '@/utils/error-handling'
import { isStringEmpty } from '@/utils/general'
import { showNotify } from '@/utils/notify'
import { DEFAULT_VALUE_FOR_FILTER } from '@/utils/okr-elements/filters'
import { numberOrNullProp } from '@/utils/prop-validators'
import { DESCRIPTION_FILED_LIMITS } from '@/utils/rich-edit'
import { selectAllIsSelected } from '@/utils/select'
import { COMMENT_FIELD_PLACEMENTS, TIPTAP_EXTENSION_NAMES } from '@/utils/tiptap-utils'

import AppEmojiPicker from '@/components/AppEmojiPicker'
import AppModalWithConfirmation from '@/components/AppModalWithConfirmation'
import FormFieldNext from '@/components/form/FormFieldNext'
import GlobalGroupsWorkspaceSelect from '@/components/global-groups/GlobalGroupsWorkspaceSelect'
import DescriptionField from '@/components/objectives/forms/DescriptionField'
import ModalFooterActions from '@/components/objectives/forms/ModalFooterActions'
import AppButtonNext from '@/components/ui/AppButtonNext/AppButtonNext'
import AppInput from '@/components/ui/AppInput/AppInput'
import SkeletonItem from '@/components/ui/SkeletonLoaders/SkeletonItem'

defineOptions({
  name: 'CheckinTemplateModal'
})

const props = defineProps({
  show: {
    type: Boolean,
    default: false
  },

  templateId: {
    validator: v => numberOrNullProp(v),
    default: null
  },

  templateContent: {
    type: String,
    default: ''
  }
})

const emit = defineEmits({
  'on-close': null,
  'template-saved': null
})

const { t } = useI18n()

const TITLE_MAX_LENGTH = 63

const { EMOJI, TITLE, WORKSPACE_IDS, WORKSPACES, ADD_TO_ALL_WORKSPACES, TEXT } =
  CHECKIN_TEMPLATE_ENTITY_KEYS

const DEFAULT_FORM_MODEL = {
  [EMOJI]: '🗂️',
  [TITLE]: '',
  [WORKSPACE_IDS]: cloneDeep(DEFAULT_VALUE_FOR_FILTER),
  [TEXT]: ''
}

const appModalWithConfirmationReference = useTemplateRef('appModalWithConfirmation')
const emojiPickerRef = useTemplateRef('emojiPickerRef')

const formModel = ref(cloneDeep(DEFAULT_FORM_MODEL))
const templateSnapshot = ref(cloneDeep(DEFAULT_FORM_MODEL))

const loading = ref(false)

const isEdit = computed(() => !isNull(props.templateId))

const modalTitle = computed(() => {
  return isEdit.value ? t('checkins.edit_template') : t('checkins.new_template')
})

const areDataChanged = computed(() => {
  return !isEqual(formModel.value, templateSnapshot.value)
})

const isTitleEmpty = computed(() => isStringEmpty(formModel.value[TITLE]))

const isWorkspacesEmpty = computed(() => {
  return isEmpty(formModel.value[WORKSPACE_IDS])
})

const isContentLimitExceeded = ref(false)

const isSubmitDisabled = computed(() => {
  return isTitleEmpty.value || isWorkspacesEmpty.value || isContentLimitExceeded.value
})

const isTemplateDataLoading = ref(false)

const initEditMode = async () => {
  const api = new CheckinTemplatesApiHandler()
  isTemplateDataLoading.value = true
  try {
    const templateData = await api.getTemplateById(props.templateId)
    formModel.value = {
      ...formModel.value,
      [EMOJI]: templateData[EMOJI],
      [TITLE]: templateData[TITLE],
      [TEXT]: templateData[TEXT] || DEFAULT_FORM_MODEL[TEXT],
      [WORKSPACE_IDS]: templateData[WORKSPACES].map(({ id }) => id)
    }
    templateSnapshot.value = cloneDeep(formModel.value)
  } catch (error) {
    handleError({ error })
    handleModalClose()
  } finally {
    isTemplateDataLoading.value = false
  }
}

watch(
  () => props.show,
  newValue => {
    if (!newValue) return

    if (isEdit.value) {
      initEditMode()
    }

    if (!isEdit.value && !isStringEmpty(props.templateContent)) {
      formModel.value[TEXT] = props.templateContent
    }
  }
)

const resetForm = () => {
  formModel.value = cloneDeep(DEFAULT_FORM_MODEL)
  templateSnapshot.value = cloneDeep(DEFAULT_FORM_MODEL)
  isContentLimitExceeded.value = false
}

const handleModalClose = () => {
  emit('on-close')
  resetForm()
}

const handleCancel = () => {
  appModalWithConfirmationReference.value?.close()
}

const handleEmojiSelect = emoji => {
  formModel.value[EMOJI] = emoji
  emojiPickerRef.value.togglePicker()
}

const handleSave = async () => {
  if (isSubmitDisabled.value) return

  loading.value = true

  try {
    const api = new CheckinTemplatesApiHandler()

    const isAllWorkspacesSelected = selectAllIsSelected(formModel.value[WORKSPACE_IDS])

    const payload = {
      ...toValue(formModel),
      [ADD_TO_ALL_WORKSPACES]: isAllWorkspacesSelected
    }

    payload[WORKSPACE_IDS] = isAllWorkspacesSelected ? [] : formModel.value[WORKSPACE_IDS]

    const requestMethod = isEdit.value
      ? () => api.updateTemplate(props.templateId, payload)
      : () => api.createTemplate(payload)

    const templateData = await requestMethod()

    emit('template-saved', templateData)

    handleModalClose()

    const messageKey = isEdit.value
      ? 'checkins.template.updated_message'
      : 'checkins.template.created_message'

    showNotify({
      title: t(messageKey)
    })

    const eventName = isEdit.value
      ? EVENT_NAMES.CHECKIN_TEMPLATE_UPDATED
      : EVENT_NAMES.CHECKIN_TEMPLATE_CREATED

    gtmTracker.logEvent(eventName)
  } catch (error) {
    handleError({ error })
  } finally {
    loading.value = false
  }
}
</script>

<style lang="scss" scoped>
.ctm-ContentWrapper {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.ctm-TitleIconWrapper {
  display: flex;
  gap: 5px;
  align-items: center;
}

.ctm-IconButton {
  width: 40px;
  flex-shrink: 0;
}

.ctm-TitleInput {
  width: 100%;
}

.ctm-DescriptionField {
  width: 100%;
  --description-field-margin-bottom: 0;
  --editor-active-outer-padding: 8px;
}
</style>
